import './CourseFeedCard.scss';

import { useEffect, useMemo, useState } from 'react';

import { Carousel } from 'antd';
import { FaStar } from 'react-icons/fa6';
import { HiBookOpen } from 'react-icons/hi2';

import { useAppNavigate, useAppSelector } from '../../../../shared/hooks';
import { hasFeatureAccess } from '../../../../shared/utils';
import { FEATURES } from '../../../../types/baseTypes';
import { ICourse, ICourseCategory } from '../../../../types/courseTypes';
import { ROUTES } from '../../../../types/routes';
import CourseBigCard from './CourseBigCard';
import { getCoursesForFeedCards } from './courseFeedCardUtils';
import CourseSmallCard from './CourseSmallCard';

const CourseFeedCard = () => {
  const navigate = useAppNavigate();

  const { hostMetadata } = useAppSelector((state) => state.app);

  const [latestEditionsCourses, setLatestEditionsCourses] = useState<ICourse[]>(
    [],
  );
  const [recommendedCourses, setRecommendedCourses] = useState<ICourse[]>([]);
  const [categories, setCategories] = useState<ICourseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const creatorId = hostMetadata.creator._id;

  const getLatestEditionsCourses = async () => {
    const courses = await getCoursesForFeedCards({
      objQuery: {
        creator: creatorId,
        isPublished: true,
        sortBy: 'publishDate',
        sortOrder: 'desc',
        searchBy: 'course',
        page: 1,
        limit: 3,
        onlySubscribed: true,
      },
    });
    setLatestEditionsCourses(courses);
  };

  const getRecommendedCourses = async () => {
    const courses = await getCoursesForFeedCards({
      objQuery: {
        creator: creatorId,
        sortBy: 'publishDate',
        sortOrder: 'desc',
        searchBy: 'course',
        page: 1,
        limit: 3,
        onlySubscribed: true,
        isPublished: true,
        isRecommendationEnabled: true,
      },
    });
    setRecommendedCourses(courses);
    const categoriesById = new Map<string, ICourseCategory>();
    for (const course of courses) {
      const cat = course.category;
      if (!categoriesById.has(cat._id)) {
        categoriesById.set(cat._id, cat);
      }
    }
    setCategories([...categoriesById.values()]);
  };

  const latestEditionsCoursesTitle = useMemo(() => {
    return (
      <div className="courses-title">
        <div className="courses-title__icon">
          <HiBookOpen />{' '}
        </div>
        Latest
      </div>
    );
  }, []);

  const recommendedCoursesTitle = useMemo(() => {
    return (
      <div className="courses-title">
        <div className="courses-title__icon">
          <FaStar />{' '}
        </div>
        Recommended Courses
      </div>
    );
  }, []);

  const filteredRecommendedCourses = useMemo(() => {
    if (!selectedCategory) {
      return recommendedCourses;
    }
    return recommendedCourses.filter(
      (course) => course.category._id === selectedCategory,
    );
  }, [recommendedCourses, selectedCategory]);

  useEffect(() => {
    getLatestEditionsCourses();
    getRecommendedCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayLatestEditionsCourses = useMemo(() => {
    if (
      latestEditionsCourses.length === 0 ||
      !hasFeatureAccess(FEATURES.COURSE_RECOMMENDATION)
    ) {
      return null;
    }
    return (
      <div className="courses-container">
        {latestEditionsCoursesTitle}
        <div className="courses-list">
          <Carousel autoplay className="courses-carousel">
            {latestEditionsCourses.map((course) => (
              <CourseBigCard
                key={course._id}
                course={course}
                onPress={() => {
                  navigate(ROUTES.COURSE_OVERVIEW, {
                    courseId: course._id,
                  });
                }}
              />
            ))}
          </Carousel>
        </div>
      </div>
    );
  }, [latestEditionsCourses, latestEditionsCoursesTitle, navigate]);

  const displayRecommendedCourses = useMemo(() => {
    if (!hasFeatureAccess(FEATURES.COURSE_RECOMMENDATION)) {
      return null;
    }
    if (recommendedCourses.length === 0) {
      return null;
    }
    return (
      <div className="courses-container">
        {recommendedCoursesTitle}
        <div className="categories-list">
          {categories.map((category) => (
            <div
              key={category._id}
              className={`category-item ${selectedCategory === category._id ? 'selected' : ''}`}
              onClick={() => {
                if (selectedCategory === category._id) {
                  setSelectedCategory(null);
                } else {
                  setSelectedCategory(category._id);
                }
              }}>
              {category.name?.toUpperCase()}
            </div>
          ))}
        </div>
        <div className="courses-list">
          {filteredRecommendedCourses.map((course) => (
            <CourseSmallCard
              key={course._id}
              course={course}
              onPress={() => {
                navigate(ROUTES.COURSE_OVERVIEW, {
                  courseId: course._id,
                });
              }}
            />
          ))}
        </div>
      </div>
    );
  }, [
    categories,
    filteredRecommendedCourses,
    navigate,
    recommendedCourses.length,
    recommendedCoursesTitle,
    selectedCategory,
  ]);

  return (
    <div>
      {displayLatestEditionsCourses}
      {displayRecommendedCourses}
    </div>
  );
};

export default CourseFeedCard;
